import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center text-sm font-semibold tracking-tight transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'rounded-btn bg-fg-1 text-bg-surface hover:opacity-90',
        outline:
          'rounded-btn border border-border-default bg-bg-surface text-fg-2 hover:border-border-strong hover:bg-bg-subtle',
        ghost: 'rounded-btn text-fg-2 hover:bg-bg-muted',
        mindexia:
          'rounded-btn text-fg-on-brand [background:var(--gradient-brand)] hover:brightness-105 hover:-translate-y-px focus-visible:ring-purple-500/40',
        mindexiaOutline:
          'rounded-btn border border-border-brand bg-bg-surface text-fg-brand hover:border-purple-400 hover:bg-purple-50 focus-visible:ring-purple-500/30',
        sso: 'rounded-pill border border-border-default bg-bg-surface text-fg-2 hover:bg-bg-subtle hover:border-border-strong hover:shadow-xs focus-visible:ring-purple-500/30',
      },
      size: {
        default: 'h-10 px-5',
        sm: 'h-9 px-4',
        lg: 'h-11 px-6',
        pill: 'h-11 px-5',
        sso: 'h-11 px-[18px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, ...props },
  ref,
) {
  return (
    <button ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  );
});

export { buttonVariants };
